// Formato EAN-13 de etiqueta de balança: prefixo 2, código do produto,
// peso/preço e dígito final. Não exige validar o dígito para manter a leitura
// das etiquetas já aceitas pelo PDV.
const DEFAULT_CONFIG = Object.freeze({
  habilitada: false,
  tipo: "peso",
  tamanhoCodigoProduto: 6,
  casasDecimais: 3,
});

function isTrue(value) {
  return value === true || value === 1 || value === "1" || value === "true";
}

function inteiroNoIntervalo(value, fallback, min, max) {
  const numero = Number(value);
  return Number.isInteger(numero) && numero >= min && numero <= max
    ? numero
    : fallback;
}

function normalizarConfiguracao(config = {}) {
  config = config || {};
  return {
    habilitada: isTrue(config.habilitada ?? config.usar_codigo_balanca),
    tipo: (config.tipo ?? config.balanca_etiqueta_tipo) === "preco" ? "preco" : "peso",
    tamanhoCodigoProduto: inteiroNoIntervalo(
      config.tamanhoCodigoProduto ?? config.balanca_tamanho_codigo_produto,
      DEFAULT_CONFIG.tamanhoCodigoProduto,
      1,
      10
    ),
    casasDecimais: inteiroNoIntervalo(
      config.casasDecimais ?? config.balanca_casas_decimais,
      DEFAULT_CONFIG.casasDecimais,
      0,
      5
    ),
  };
}

function interpretarEtiqueta(codigo, configuracao = {}) {
  const config = normalizarConfiguracao(configuracao);
  const leitura = String(codigo || "").trim();
  if (!config.habilitada || !/^2\d{12}$/.test(leitura)) return null;

  const fimProduto = 1 + config.tamanhoCodigoProduto;
  const codigoProduto = leitura.slice(1, fimProduto);
  const valorBruto = Number(leitura.slice(fimProduto, 12));
  if (!Number.isFinite(valorBruto) || valorBruto <= 0) return null;

  return {
    codigo: leitura,
    codigoBase: leitura.slice(0, fimProduto),
    codigoProduto,
    tipo: config.tipo,
    valor: valorBruto / Math.pow(10, config.tipo === "preco" ? 2 : config.casasDecimais),
  };
}

// Código gerado pela carga antiga da balança: 2 + código interno (4 dígitos)
// + 00 + peso em gramas (5 dígitos) + dígito final.
function interpretarEtiquetaLegada(codigo, configuracao = {}) {
  if (normalizarConfiguracao(configuracao).habilitada) return null;
  const leitura = String(codigo || "").trim();
  if (!/^2\d{12}$/.test(leitura) || leitura.slice(5, 7) !== "00") return null;

  const valorBruto = Number(leitura.slice(7, 12));
  if (!Number.isFinite(valorBruto) || valorBruto <= 0) return null;

  return {
    codigo: leitura,
    codigoBase: leitura.slice(0, 7),
    codigoProduto: leitura.slice(1, 5),
    tipo: "peso",
    valor: valorBruto / 1000,
    legada: true,
  };
}

function interpretarEtiquetaParaVenda(codigo, configuracao = {}) {
  return normalizarConfiguracao(configuracao).habilitada
    ? interpretarEtiqueta(codigo, configuracao)
    : interpretarEtiquetaLegada(codigo, configuracao);
}

function interpretarEtiquetaProduto(codigo, configuracao, produto) {
  const etiqueta = interpretarEtiquetaParaVenda(codigo, configuracao);
  if (!etiqueta || !produto) return null;

  if (etiqueta.legada &&
      String(produto?.unidade_medida?.sigla || "").toUpperCase() !== "KG") {
    return null;
  }

  const codigos = codigosParaBusca(etiqueta);
  const codigoInterno = etiqueta.codigoProduto.replace(/^0+/, "") || "0";
  const identificadores = [produto?.cod_barras, produto?.referencia, produto?.codigo]
    .filter((valor) => valor !== null && valor !== undefined && String(valor).trim() !== "")
    .map((valor) => String(valor).trim());
  const codigoConfere = identificadores.some((valor) =>
    codigos.includes(valor) || valor === codigoInterno
  );

  // A busca também pode localizar pelo código interno. A conferência impede
  // que uma leitura anterior seja aplicada a outro produto selecionado.
  const produtoPesadoSemCodigo = identificadores.length === 0 &&
    isTrue(produto.pesado) &&
    String(produto?.unidade_medida?.sigla || "").toUpperCase() === "KG";
  return codigoConfere || produtoPesadoSemCodigo
    ? etiqueta
    : null;
}

function codigosParaBusca(etiqueta) {
  if (!etiqueta) return [];
  const semZeros = etiqueta.codigoProduto.replace(/^0+/, "") || "0";
  const codigos = [etiqueta.codigoBase, etiqueta.codigoProduto, semZeros];
  if (Number(etiqueta.codigoProduto) <= 9999) {
    codigos.push(`2${String(Number(etiqueta.codigoProduto) * 10).padStart(5, "0")}0`);
  }
  return [...new Set(codigos)];
}

function calcularItemEtiqueta(etiqueta, precoUnitario, unidade = "KG") {
  if (!etiqueta) return null;
  let quantidade = etiqueta.valor;
  let valorUnitario = Number(precoUnitario);
  if (etiqueta.tipo === "preco") {
    if (String(unidade || "").toUpperCase() === "UN") {
      return { quantidade: 1, valorUnitario: etiqueta.valor };
    }
    if (!Number.isFinite(valorUnitario) || valorUnitario <= 0) return null;
    quantidade = Number((etiqueta.valor / valorUnitario).toFixed(4));
    if (quantidade <= 0) return null;
    if (Number((quantidade * valorUnitario).toFixed(2)) !== etiqueta.valor) {
      valorUnitario = etiqueta.valor / quantidade;
    }
  }
  return { quantidade, valorUnitario };
}

module.exports = {
  DEFAULT_CONFIG,
  normalizarConfiguracao,
  interpretarEtiqueta,
  interpretarEtiquetaLegada,
  interpretarEtiquetaParaVenda,
  interpretarEtiquetaProduto,
  codigosParaBusca,
  calcularItemEtiqueta,
};
